/**
 * Recently Viewed Products Web Component
 * 
 * PP-676/PP-734: Added validation to prevent empty product tiles
 * - Validates product card contains actual title before displaying
 * - Cleans up invalid handles from cookie to prevent future issues
 * - Increased cookie storage from 8 to 12 for better buffer against invalid products
 */
if (!customElements.get("recently-viewed")) {
  // Maximum number of products to store in the cookie
  // Increased from 8 to 12 to provide buffer when some products become invalid
  const MAX_STORED_PRODUCTS = 12;

  class RecentlyViewed extends HTMLElement {
    constructor() {
      super();
      this.cookie = null;
      this.productHandle = null;
      this.productSlider = null;
    }

    connectedCallback() {
      this.container = this.querySelector(".js-recently-viewed__container");
      this.productHandle = this.dataset.productHandle;
      this.productSlider = this.querySelector("product-slider");

      this.getCookie();

      this.setCookie();

      this.populateSlider();
    }

    setCookie() {
      if (!this.productHandle) return;

      let cookieSet = new Set(this.cookie);
      cookieSet = new Set([this.productHandle, ...cookieSet]);
      this.cookie = Array.from(cookieSet).slice(0, MAX_STORED_PRODUCTS);
      this.writeCookie();
    }

    /**
     * Write the current cookie array to the browser cookie
     */
    writeCookie() {
      const date = new Date();
      date.setTime(date.getTime() + 30 * 24 * 60 * 60 * 1000);
      const expires = `expires=${date.toUTCString()}`;
      document.cookie = `recentlyViewed=${JSON.stringify(this.cookie)}; ${expires}; path=/`;
    }

    getCookie() {
      const cookie = document.cookie.split("; ").find((row) => row.startsWith("recentlyViewed"));

      try {
        this.cookie = cookie ? JSON.parse(cookie.split("=")[1]) : [];
        if (!Array.isArray(this.cookie)) throw new Error("Invalid cookie format");
      } catch (error) {
        console.error("Error parsing recently viewed cookie:", error);
        this.cookie = [];
      }
    }

    async populateSlider() {
      this.container.innerHTML = "";
      
      const results = await Promise.all(
        this.cookie.map(async (productHandle) => {
          const slide = await RecentlyViewed.fetchProduct(productHandle);
          return { handle: productHandle, slide };
        })
      );

      // Filter out invalid products and collect valid handles
      const validResults = results.filter((result) => result.slide !== "");
      const validSlides = validResults.map((result) => result.slide);
      const validHandles = validResults.map((result) => result.handle);

      // Update cookie if some products were invalid (cleanup)
      if (validHandles.length !== this.cookie.length) {
        this.cookie = validHandles;
        this.writeCookie();
      }

      this.container.innerHTML = validSlides.join("");

      if (this.productSlider && typeof this.productSlider.initSwiper === "function") {
        this.productSlider.initSwiper();
      }
    }

    /**
     * Fetch a product card from the server and validate it contains actual data
     * 
     * PP-676/PP-734: Shopify returns HTTP 200 even for non-existing products,
     * so we need to validate the content, not just the response status.
     * 
     * @param {string} productHandle - The product handle to fetch
     * @returns {string} The product slide HTML or empty string if invalid
     */
    static async fetchProduct(productHandle) {
      try {
        const response = await fetch(
          `${window.Shopify.routes.root}products/${productHandle}?section_id=last-viewed-product`,
        );
        
        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const slide = html.querySelector(".js-product-slide");

        // No slide found - product template didn't render anything (new validation)
        if (!slide) {
          return "";
        }

        // Validate the product card has actual content
        // Check for product title - if empty, the product doesn't exist or has no data
        const title = slide.querySelector(".product-card__title");
        if (!title || !title.textContent.trim()) {
          return "";
        }

        // Check for product ID - another indicator of valid product
        const productId = slide.querySelector("[product-id]")?.getAttribute("product-id");
        if (!productId) {
          return "";
        }

        return slide.outerHTML;
      } catch (error) {
        console.error("Error fetching recently viewed product:", productHandle, error);
        return "";
      }
    }
  }

  customElements.define("recently-viewed", RecentlyViewed);
}
